import { getInstance } from "../instance";
import { OperatorIdentity, operatorRequestConfig } from "../operatorHeaders";
import {
  DASHBOARD_VISIBILITY_REFUSAL_STATUS,
  DASHBOARD_VISIBILITY_REQUEST_FAILED,
  DashboardVisibilityRefusal,
  DashboardVisibilityRefusalCode,
  DashboardVisibilityResponse,
  DashboardVisibilityUpdate,
} from "./types";

/**
 * ABO view and edit of a member's Books / Magazines visibility in the member
 * app (abo-hide-books-rm). The endpoints are versioned (`/v1/...`), so we use
 * a RELATIVE path on the shared `dsInstance` whose baseURL already carries
 * `/v1` (same pattern as `api/memberCredits/actions.ts`).
 */
const instance = getInstance();

/** The member's stored and effective visibility. Needs no operator token. */
export const getDashboardVisibility = async (
  customerId: string
): Promise<DashboardVisibilityResponse> => {
  return instance
    .get<DashboardVisibilityResponse>(
      `/abo/customers/${customerId}/dashboard-visibility`
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

/**
 * Store the operator's choice for the sections in `body` (only the changed
 * ones). DS verifies the operator from the FusionAuth id token in
 * `x-afy-operator-token`, with no role check (ADR-004). The operator headers
 * go on THIS request only, never on the shared instance. Read failures with
 * `getDashboardVisibilityRefusal`.
 */
export const putDashboardVisibility = async (
  customerId: string,
  body: DashboardVisibilityUpdate,
  operator: OperatorIdentity
): Promise<DashboardVisibilityResponse> => {
  return instance
    .put<DashboardVisibilityResponse>(
      `/abo/customers/${customerId}/dashboard-visibility`,
      body,
      operatorRequestConfig(operator)
    )
    .then((response) => response.data)
    .catch((error) => Promise.reject(error));
};

const isRefusalCode = (code: unknown): code is DashboardVisibilityRefusalCode =>
  typeof code === "string" &&
  Object.prototype.hasOwnProperty.call(
    DASHBOARD_VISIBILITY_REFUSAL_STATUS,
    code
  );

const readObject = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;

/**
 * The typed refusal behind a failed GET or PUT. A known `code` is recognised
 * only on the status DS answers it with; anything else (a 500, a 400, a
 * network error, a value that is not an error) is `REQUEST_FAILED`, with the
 * status when there was one. Never throws. Structural on purpose: it reads
 * the axios error shape without depending on `axios.isAxiosError`.
 */
export const getDashboardVisibilityRefusal = (
  error: unknown
): DashboardVisibilityRefusal => {
  const response = readObject(readObject(error)?.response);
  const status =
    typeof response?.status === "number" ? response.status : null;
  const data = readObject(response?.data);
  const code = data?.code;
  if (
    !isRefusalCode(code) ||
    DASHBOARD_VISIBILITY_REFUSAL_STATUS[code] !== status
  ) {
    return { code: DASHBOARD_VISIBILITY_REQUEST_FAILED, message: null, status };
  }
  const message =
    typeof data?.message === "string" && data.message.trim()
      ? data.message
      : null;
  return { code, message, status };
};
